import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import ConversationList from './components/ConversationList';
import ChatWindow from './components/ChatWindow';
import MemberDirectory from './components/MemberDirectory';

const MemberChatSystem = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading, userProfile } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDirectory, setShowDirectory] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadConversations();
    }
  }, [user, currentTenant]);

  useEffect(() => {
    if (activeConversation?.id) {
      loadMessages(activeConversation?.id);
      subscribeToMessages(activeConversation?.id);
    }
  }, [activeConversation]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('conversations')
        ?.select(`
          *,
          participant1:user_profiles!conversations_participant1_id_fkey(id, full_name, avatar_url),
          participant2:user_profiles!conversations_participant2_id_fkey(id, full_name, avatar_url),
          last_message:messages(content, created_at, sender_id)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.or(`participant1_id.eq.${user?.id},participant2_id.eq.${user?.id}`)
        ?.order('updated_at', { ascending: false });

      if (error) throw error;

      const formattedConversations = data?.map(conv => {
        const otherParticipant = conv?.participant1?.id === user?.id ? conv?.participant2 : conv?.participant1;
        const lastMsg = conv?.last_message?.[0];
        
        return {
          id: conv?.id,
          participant: otherParticipant,
          lastMessage: lastMsg?.content || 'No messages yet',
          lastMessageTime: lastMsg?.created_at,
          unreadCount: conv?.unread_count || 0,
          isOnline: false
        };
      });

      setConversations(formattedConversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const { data, error } = await supabase
        ?.from('messages')
        ?.select(`
          *,
          sender:user_profiles(id, full_name, avatar_url)
        `)
        ?.eq('conversation_id', conversationId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const subscribeToMessages = (conversationId) => {
    const channel = supabase
      ?.channel(`messages-${conversationId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        async (payload) => {
          const { data: senderData } = await supabase
            ?.from('user_profiles')
            ?.select('id, full_name, avatar_url')
            ?.eq('id', payload?.new?.sender_id)
            ?.single();

          setMessages(prev => [...prev, { ...payload?.new, sender: senderData }]);
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  };

  const handleSendMessage = async (content) => {
    if (!content?.trim() || !activeConversation?.id) return;

    try {
      const { error } = await supabase
        ?.from('messages')
        ?.insert({
          conversation_id: activeConversation?.id,
          sender_id: user?.id,
          content: content?.trim(),
          tenant_id: currentTenant?.id
        });

      if (error) throw error;

      // Update conversation timestamp
      await supabase
        ?.from('conversations')
        ?.update({ updated_at: new Date()?.toISOString() })
        ?.eq('id', activeConversation?.id);

    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleStartConversation = async (memberId) => {
    try {
      // Check if conversation already exists
      const { data: existing } = await supabase
        ?.from('conversations')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.or(`and(participant1_id.eq.${user?.id},participant2_id.eq.${memberId}),and(participant1_id.eq.${memberId},participant2_id.eq.${user?.id})`)
        ?.single();

      if (existing) {
        const { data: participant } = await supabase
          ?.from('user_profiles')
          ?.select('id, full_name, avatar_url')
          ?.eq('id', memberId)
          ?.single();

        setActiveConversation({
          id: existing?.id,
          participant
        });
        setShowDirectory(false);
        return;
      }

      // Create new conversation
      const { data: newConv, error } = await supabase
        ?.from('conversations')
        ?.insert({
          tenant_id: currentTenant?.id,
          participant1_id: user?.id,
          participant2_id: memberId
        })
        ?.select()
        ?.single();

      if (error) throw error;

      const { data: participant } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, avatar_url')
        ?.eq('id', memberId)
        ?.single();

      setActiveConversation({
        id: newConv?.id,
        participant
      });
      setShowDirectory(false);
      loadConversations();
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  const filteredConversations = conversations?.filter(conv =>
    conv?.participant?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Conversations Sidebar */}
        <div className={`w-full lg:w-80 xl:w-96 bg-card border-r border-border flex flex-col ${
          activeConversation && 'hidden lg:flex'
        }`}>
          {/* Search Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-2 mb-4">
              <h1 className="text-2xl font-heading font-bold text-foreground flex-1">
                Messages
              </h1>
              <button
                onClick={() => setShowDirectory(true)}
                className="p-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth"
                aria-label="New conversation"
              >
                <Icon name="Plus" size={20} />
              </button>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="Search" size={18} color="var(--color-muted-foreground)" />
              </div>
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Icon name="Loader2" size={32} className="animate-spin text-primary" />
              </div>
            ) : filteredConversations?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <Icon name="MessageCircle" size={48} color="var(--color-muted-foreground)" className="mb-4" />
                <p className="text-muted-foreground mb-2">No conversations yet</p>
                <p className="text-sm text-muted-foreground">Start chatting with gym members</p>
              </div>
            ) : (
              <ConversationList
                conversations={filteredConversations}
                activeConversation={activeConversation}
                onSelectConversation={setActiveConversation}
                currentUserId={user?.id}
              />
            )}
          </div>
        </div>

        {/* Chat Window */}
        <div className={`flex-1 flex flex-col ${
          !activeConversation && 'hidden lg:flex'
        }`}>
          {activeConversation ? (
            <ChatWindow
              conversation={activeConversation}
              messages={messages}
              currentUser={userProfile}
              onSendMessage={handleSendMessage}
              onBack={() => setActiveConversation(null)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center bg-muted/20">
              <div className="text-center">
                <Icon name="MessageSquare" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-foreground mb-2">Select a conversation</h2>
                <p className="text-muted-foreground">Choose a conversation from the list to start messaging</p>
              </div>
            </div>
          )}
        </div>

        {/* Member Directory Modal */}
        {showDirectory && (
          <MemberDirectory
            tenantId={currentTenant?.id}
            currentUserId={user?.id}
            onSelectMember={handleStartConversation}
            onClose={() => setShowDirectory(false)}
          />
        )}
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default MemberChatSystem;